import {
  CropRegion,
  LayoutSegmentCaption,
  LayoutSegmentSlot,
} from '@sparemin/api-sdk';
import { isEmpty } from 'underscore';
import { LayoutSegmentPatch } from 'redux/modules/embed/types';
import { Dimensions, Position, ScenePreset, Size } from 'types';
import bem from 'utils/bem';
import { FRAMED_LAYOUT_MAX_SLOTS } from 'utils/constants';
import measurement, { ViewportHeight, ViewportWidth } from 'utils/measurement';
import { clamp } from 'utils/numbers';
import { fitElement, stringToPx } from 'utils/placement';
import {
  captionsOverrideSelector,
  isBackgroundBlurDirtySelector,
  isBackgroundBlurEnabledSelector,
  isCaptionsOverrideDirtySelector,
  slotCountSelector,
  slotsSelector,
} from './state/selectors';
import { EditSceneModalState, SceneCaptionsOverride } from './state/types';

export const block = bem('edit-scene-modal');

function clampSlotCount(nSlots: number) {
  return clamp(nSlots, 0, FRAMED_LAYOUT_MAX_SLOTS);
}

function roundCrop(crop: CropRegion): CropRegion {
  return {
    left: Math.round(crop.left),
    top: Math.round(crop.top),
    right: Math.round(crop.right),
    bottom: Math.round(crop.bottom),
  };
}

function getSlotGrid(nSlots: number) {
  const slotCount = clampSlotCount(nSlots);

  // maximum number of columns needed.  each row should have the max number of
  // columns with the last row possibly being partially filled
  const cols = slotCount <= 2 ? 1 : 2;

  // total number of rows needed for the layout
  const rows = Math.ceil(slotCount / cols);

  return { cols, rows };
}

// https://sparemin.atlassian.net/wiki/spaces/PROD/pages/3661299735/Scene+Editor+Design+Spec+v2?focusedCommentId=3674144770
export function createSlots(nSlots: number, frameDimensions: Size<number>) {
  const { height: frameHeight, width: frameWidth } = frameDimensions;

  const slotCount = clampSlotCount(nSlots);

  const { cols, rows } = getSlotGrid(nSlots);

  const frameAspectRatio = frameWidth / frameHeight;

  const frameWidthVw = measurement(100, 'vw');
  const frameHeightVw = frameWidthVw.divideBy(frameAspectRatio);

  const slotHeightVw = frameHeightVw.divideBy(rows);
  const slotWidthVw = frameWidthVw.divideBy(cols);

  const slots: Pick<LayoutSegmentSlot, 'position' | 'style'>[] = [];

  for (let row = 0; row < rows; row += 1) {
    const nSlotsInRow = Math.min(cols, slotCount - row * cols);
    const slotsInRowWidth = slotWidthVw.times(nSlotsInRow);
    const colOffset = frameWidthVw.minus(slotsInRowWidth).divideBy(2);

    for (let col = 0; col < nSlotsInRow; col += 1) {
      slots.push({
        position: {
          top: slotHeightVw
            .times(row)
            .toUnit('vh', frameDimensions)
            .toString(),
          left: colOffset
            .plus(slotWidthVw.times(col))
            .toUnit('vw', frameDimensions)
            .toString(),
        },
        style: {
          height: slotHeightVw.toUnit('vh', frameDimensions).toString(),
          width: slotWidthVw.toUnit('vw', frameDimensions).toString(),
        },
      });
    }
  }

  return slots;
}

function createFitLayoutSlots(
  frameDimensions: Size<number>,
  videoDimensions: Size<number>,
) {
  const fitted = fitElement(videoDimensions, frameDimensions, 'fit');

  return [
    {
      position: {
        top: fitted.top.toUnit('vh', frameDimensions).toString(),
        left: fitted.left.toUnit('vw', frameDimensions).toString(),
      },
      staticCrop: roundCrop({
        left: 0,
        top: 0,
        right: videoDimensions.width,
        bottom: videoDimensions.height,
      }),
      style: {
        width: fitted.width.toUnit('vw', frameDimensions).toString(),
        height: fitted.height.toUnit('vh', frameDimensions).toString(),
      },
    },
  ];
}

export function createSlotsForPreset(
  preset: ScenePreset,
  frameDimensions: Size<number>,
  videoDimensions: Size<number>,
) {
  switch (preset) {
    case ScenePreset.CURRENT: {
      return [];
    }

    case ScenePreset.FIT: {
      return createFitLayoutSlots(frameDimensions, videoDimensions);
    }

    default: {
      return createSlots(preset, frameDimensions);
    }
  }
}

type VerticalAnchor = 'top' | 'center' | 'bottom';
type HorizontalAnchor = 'left' | 'center' | 'right';

type Anchor = { x: HorizontalAnchor; y: VerticalAnchor };

/**
 * Calculates horizontal position based on anchor strategy
 */
function getHorizontalPosition(
  anchor: HorizontalAnchor,
  boundsLeft: number,
  boundsCenterX: number,
  boundsRight: number,
  cropWidth: number,
): number {
  switch (anchor) {
    case 'left':
      return boundsLeft;
    case 'right':
      return boundsRight - cropWidth;
    default:
      return boundsCenterX - cropWidth / 2;
  }
}

/**
 * Calculates vertical position based on anchor strategy
 */
function getVerticalPosition(
  anchor: VerticalAnchor,
  boundsTop: number,
  boundsCenterY: number,
  boundsBottom: number,
  cropHeight: number,
): number {
  switch (anchor) {
    case 'top':
      return boundsTop;
    case 'bottom':
      return boundsBottom - cropHeight;
    default:
      return boundsCenterY - cropHeight / 2;
  }
}

export type CropSubjectOptions = {
  anchor?: Anchor;
};

function cropSubjectForSlot(
  subject: CropRegion,
  slot: Pick<LayoutSegmentSlot, 'position' | 'style'>,
  frameDimensions: Size<number>,
  videoDimensions: Size<number>,
  opts?: CropSubjectOptions,
) {
  const { anchor = { x: 'center', y: 'center' } } = opts ?? {};

  // Calculate subject bounds
  const subjectWidth = subject.right - subject.left;
  const subjectHeight = subject.bottom - subject.top;
  const subjectCenterX = (subject.left + subject.right) / 2;
  const subjectCenterY = (subject.top + subject.bottom) / 2;

  // Calculate slot dimensions
  const { width: slotWidth, height: slotHeight } = stringToPx(
    slot.style,
    frameDimensions,
  );

  // Calculate crop dimensions that fit the slot's aspect ratio within subject bounds
  const scaleFactor = Math.min(
    subjectWidth / slotWidth,
    subjectHeight / slotHeight,
  );

  const cropWidth = slotWidth * scaleFactor;
  const cropHeight = slotHeight * scaleFactor;

  // Calculate maximum allowed positions so that crop stays within the video bounds
  const maxLeft = videoDimensions.width - cropWidth;
  const maxTop = videoDimensions.height - cropHeight;

  // Calculate the target left and top positions.  These might be "out of bounds"
  const targetLeft = getHorizontalPosition(
    anchor.x,
    0,
    subjectCenterX,
    videoDimensions.width,
    cropWidth,
  );
  const targetTop = getVerticalPosition(
    anchor.y,
    0,
    subjectCenterY,
    videoDimensions.height,
    cropHeight,
  );

  // Clamp position to ensure it stays within the video bounds
  const left = clamp(targetLeft, 0, maxLeft);
  const top = clamp(targetTop, 0, maxTop);

  return roundCrop({
    left,
    top,
    right: left + cropWidth,
    bottom: top + cropHeight,
  });
}

function selectSubjectForSlot(
  slots: FillableSlot[],
  subjects: CropRegion[],
  frameDimensions: Size<number>,
  videoDimensions: Size<number>,
  slotIndex: number,
): CropRegion {
  const detectedSubject = subjects[slotIndex % subjects.length];

  // if detedcted subject id found, use it, if not, create a deafult crop region
  // by centering the video and fitting it into the slot.  if there's no detectedSubject,
  // the subjects array is probably empty
  if (detectedSubject) {
    return detectedSubject;
  }

  const slot = slots[slotIndex];

  const slotWidth = measurement(slot.style.width).toUnit('px', frameDimensions)
    .value;
  const slotHeight = measurement(slot.style.height).toUnit(
    'px',
    frameDimensions,
  ).value;

  const slotDimensions = { width: slotWidth, height: slotHeight };

  const filled = fitElement(videoDimensions, slotDimensions, 'fill');

  const scale = filled.width.value / videoDimensions.width;

  const cropWidth = slotWidth / scale;
  const cropHeight = slotHeight / scale;

  const left = Math.max(
    0,
    Math.min(
      (videoDimensions.width - cropWidth) / 2,
      videoDimensions.width - cropWidth,
    ),
  );

  const top = Math.max(
    0,
    Math.min(
      (videoDimensions.height - cropHeight) / 2,
      videoDimensions.height - cropHeight,
    ),
  );

  return roundCrop({
    left,
    top,
    right: left + cropWidth,
    bottom: top + cropHeight,
  });
}

type FillableSlot = Omit<LayoutSegmentSlot, 'staticCrop'> &
  Partial<Pick<LayoutSegmentSlot, 'staticCrop'>>;

export function fillSlots(
  slots: FillableSlot[],
  subjects: CropRegion[],
  frameDimensions: Size<number>,
  videoDimensions: Size<number>,
  opts?: CropSubjectOptions,
): LayoutSegmentSlot[] {
  return slots.map((slot, index) => {
    if (slot.staticCrop !== undefined) {
      return slot as LayoutSegmentSlot;
    }

    const subject = selectSubjectForSlot(
      slots,
      subjects,
      frameDimensions,
      videoDimensions,
      index,
    );

    return {
      ...slot,
      staticCrop: cropSubjectForSlot(
        subject,
        slot,
        frameDimensions,
        videoDimensions,
        opts,
      ),
    };
  });
}

function canRenderFrame(
  ctx: CanvasRenderingContext2D,
  videoEl: HTMLVideoElement,
) {
  return ctx && videoEl.readyState >= HTMLMediaElement.HAVE_METADATA;
}

/**
 * Given a slot, will return the rectangle of the slot in the frame of the target
 * dimensions
 */
export function getSlotRect(
  slot: LayoutSegmentSlot,
  targetDimensions: Size<number>,
) {
  const width = measurement(slot.style.width).toUnit('px', targetDimensions)
    .value;
  const height = measurement(slot.style.height).toUnit('px', targetDimensions)
    .value;
  const left = measurement(slot.position.left).toUnit('px', targetDimensions)
    .value;
  const top = measurement(slot.position.top).toUnit('px', targetDimensions)
    .value;

  return { left, top, width, height };
}

/**
 * The corners of the preview frame are rounded.  This function will return the
 * value of the border-radius rule to apply to a slot to have its corners rounded
 * to match the frame.  In some cases, we highlight slots so just allowing the slot
 * to get clipped by the corner isn't sufficient.
 */
export function getSlotBorderRadius(
  slotRect: Dimensions<number>,
  targetDimensions: Size<number>,
  roundCornerRadiusProp: string | number = 16,
): string {
  const squareCornerRadius = '0px';
  const roundCornerRadius =
    typeof roundCornerRadiusProp === 'string'
      ? roundCornerRadiusProp
      : `${roundCornerRadiusProp}px`;

  const {
    left: slotLeft,
    top: slotTop,
    width: slotWidth,
    height: slotHeight,
  } = slotRect;

  const slotRight = slotLeft + slotWidth;
  const slotBottom = slotTop + slotHeight;

  const targetLeft = 0;
  const targetTop = 0;
  const targetRight = targetDimensions.width;
  const targetBottom = targetDimensions.height;

  const result: string[] = [
    squareCornerRadius,
    squareCornerRadius,
    squareCornerRadius,
    squareCornerRadius,
  ];

  if (slotTop === targetTop && slotLeft === targetLeft) {
    result[0] = roundCornerRadius;
  }

  if (slotTop === targetTop && slotRight === targetRight) {
    result[1] = roundCornerRadius;
  }

  if (slotBottom === targetBottom && slotRight === targetRight) {
    result[2] = roundCornerRadius;
  }

  if (slotBottom === targetBottom && slotLeft === targetLeft) {
    result[3] = roundCornerRadius;
  }

  return result.join(' ');
}

export function drawSlot(
  slot: LayoutSegmentSlot,
  canvas: HTMLCanvasElement,
  videoEl: HTMLVideoElement,
) {
  const ctx = canvas.getContext('2d');

  if (!canRenderFrame(ctx, videoEl)) {
    return;
  }

  const { staticCrop } = slot;

  const { left: cropLeft, right: cropRight, top: cropTop } = staticCrop;

  const cropWidth = cropRight - cropLeft;

  const canvasDimensions = {
    height: canvas.height,
    width: canvas.width,
  };

  const {
    left: slotLeft,
    top: slotTop,
    width: slotWidth,
    height: slotHeight,
  } = getSlotRect(slot, canvasDimensions);

  const scale = slotWidth / cropWidth;

  const scaledVideoWidth = videoEl.videoWidth * scale;
  const scaledVideoHeight = videoEl.videoHeight * scale;

  const videoOffsetLeft = -cropLeft * scale;
  const videoOffsetTop = -cropTop * scale;

  ctx.save();
  ctx.beginPath();
  ctx.rect(slotLeft, slotTop, slotWidth, slotHeight);
  ctx.clip();

  ctx.drawImage(
    videoEl,
    slotLeft + videoOffsetLeft,
    slotTop + videoOffsetTop,
    scaledVideoWidth,
    scaledVideoHeight,
  );

  ctx.restore();
}

export function drawLayout(
  slots: LayoutSegmentSlot[],
  canvas: HTMLCanvasElement,
  videoEl: HTMLVideoElement,
) {
  const ctx = canvas.getContext('2d');

  if (!canRenderFrame(ctx, videoEl)) {
    return;
  }

  ctx.clearRect(0, 0, canvas.width, canvas.height);

  slots.forEach(slot => {
    drawSlot(slot, canvas, videoEl);
  });
}

/**
 * This function takes a crop region, part of which might be outside of the video
 * (e.g. blank space in the slot around the video), and it clamps it so that the
 * region fits within the video without changing the region's shape
 */
function clampCropToBounds(
  crop: CropRegion,
  videoDimensions: Size<number>,
): CropRegion {
  const { left, top, right, bottom } = crop;

  const width = right - left;
  const height = bottom - top;

  // prevent zero/negative sizes
  if (width <= 0 || height <= 0) {
    return {
      left: clamp(left, 0, videoDimensions.width),
      top: clamp(top, 0, videoDimensions.height),
      right: clamp(right, 0, videoDimensions.width),
      bottom: clamp(bottom, 0, videoDimensions.height),
    };
  }

  const result = { left, top, right, bottom };

  // clamp edges, preserving size by shifting the rect if needed
  if (result.left < 0) {
    result.right -= result.left;
    result.left = 0;
  }

  if (result.right > videoDimensions.width) {
    result.left -= result.right - videoDimensions.width;
    result.right = videoDimensions.width;
  }

  if (result.top < 0) {
    result.bottom -= result.top;
    result.top = 0;
  }

  if (result.bottom > videoDimensions.height) {
    result.top -= result.bottom - videoDimensions.height;
    result.bottom = videoDimensions.height;
  }

  // final safety clamp
  const maxLeft = Math.max(videoDimensions.width - width, 0);
  const maxTop = Math.max(videoDimensions.height - height, 0);

  result.left = clamp(result.left, 0, maxLeft);
  result.top = clamp(result.top, 0, maxTop);

  result.right = result.left + width;
  result.bottom = result.top + height;

  return result;
}

type Coordinates = {
  x: number;
  y: number;
};

/**
 * This function is used to drag a crop region within a slot, clamping it so that
 * there can't be any blank space around the video (it must fill the frame)
 */
export function translateCrop(
  crop: CropRegion,
  delta: Coordinates,
  videoDimensions: Size<number>,
): CropRegion {
  const { x: dx, y: dy } = delta;

  const width = crop.right - crop.left;
  const height = crop.bottom - crop.top;

  const maxLeft = Math.max(videoDimensions.width - width, 0);
  const maxTop = Math.max(videoDimensions.height - height, 0);

  const nextLeft = clamp(crop.left + dx, 0, maxLeft);
  const nextTop = clamp(crop.top + dy, 0, maxTop);

  return roundCrop(
    clampCropToBounds(
      {
        left: nextLeft,
        top: nextTop,
        right: nextLeft + width,
        bottom: nextTop + height,
      },
      videoDimensions,
    ),
  );
}

type CropZoom = {
  scale: number;
  anchor: Coordinates;
};

type ZoomCropOptions = {
  minWidth?: number;
  minHeight?: number;
};

/**
 * Takes a crop region and zooms around a focal point
 */
export function zoomCrop(
  crop: CropRegion,
  zoom: CropZoom,
  videoDimensions: Size<number>,
  opts?: ZoomCropOptions,
): CropRegion {
  const { scale, anchor } = zoom;

  const width = crop.right - crop.left;
  const height = crop.bottom - crop.top;

  const minWidth = opts?.minWidth ?? 1;
  const minHeight = opts?.minHeight ?? 1;

  const nextWidth = clamp(width / scale, minWidth, videoDimensions.width);
  const nextHeight = clamp(height / scale, minHeight, videoDimensions.height);

  const anchorRatioX = (anchor.x - crop.left) / width;
  const anchorRatioY = (anchor.y - crop.top) / height;

  const nextLeft = anchor.x - anchorRatioX * nextWidth;
  const nextTop = anchor.y - anchorRatioY * nextHeight;

  return roundCrop(
    clampCropToBounds(
      {
        left: nextLeft,
        top: nextTop,
        right: nextLeft + nextWidth,
        bottom: nextTop + nextHeight,
      },
      videoDimensions,
    ),
  );
}

type SlotScaleInfoProps = {
  slot: LayoutSegmentSlot;
  crop: CropRegion;
  previewDimensions: Size<number>;
  videoDimensions: Size<number>;
  maxScale?: number;
};

/**
 * Calculates data for the slot's scale, or zoom
 */
export function getSlotScaleInfo({
  slot,
  crop,
  previewDimensions,
  videoDimensions,
  maxScale,
}: SlotScaleInfoProps) {
  if (!slot || !crop || !previewDimensions || !videoDimensions) {
    return null;
  }

  const slotRect = getSlotRect(slot, previewDimensions);
  if (slotRect.width === 0 || slotRect.height === 0) {
    return null;
  }

  const cropWidth = crop.right - crop.left;
  const cropHeight = crop.bottom - crop.top;
  if (cropWidth <= 0 || cropHeight <= 0) {
    return null;
  }

  const currentScale = slotRect.width / cropWidth;

  const fillScale = Math.max(
    slotRect.width / videoDimensions.width,
    slotRect.height / videoDimensions.height,
  );

  const minScale = Math.max(fillScale, Number.EPSILON);
  const clampedMaxScale = Math.max(maxScale, minScale);

  const anchor = {
    x: (crop.left + crop.right) / 2,
    y: (crop.top + crop.bottom) / 2,
  };

  return {
    currentScale,
    minScale,
    maxScale: clampedMaxScale,
    anchor,
    slotRect,
  };
}

/**
 * Compares two crop regions for equality
 */
export function areCropsEqual(a: CropRegion, b: CropRegion, epsilon = 0) {
  return (
    Math.abs(a.left - b.left) <= epsilon &&
    Math.abs(a.top - b.top) <= epsilon &&
    Math.abs(a.right - b.right) <= epsilon &&
    Math.abs(a.bottom - b.bottom) <= epsilon
  );
}

export function getAnchorFromEvent(
  event: React.WheelEvent<HTMLDivElement>,
  slotRect: Dimensions<number>,
  crop: CropRegion,
) {
  const relativeX = (event.clientX - slotRect.left) / slotRect.width;
  const relativeY = (event.clientY - slotRect.top) / slotRect.height;

  const cropWidth = crop.right - crop.left;
  const cropHeight = crop.bottom - crop.top;

  return {
    x: crop.left + relativeX * cropWidth,
    y: crop.top + relativeY * cropHeight,
  };
}

export function normalizeAPISlots(
  slots: LayoutSegmentSlot[],
  videoDimensions: Size<number>,
): LayoutSegmentSlot[] {
  return slots?.map(slot => {
    if (!isEmpty(slot.staticCrop)) {
      return slot;
    }

    // when API data is missing staticCrop, it assumes that the entire video
    // will be rendered. filling in this data here simplifies all of the
    // downstream logic
    return {
      ...slot,
      staticCrop: {
        left: 0,
        top: 0,
        right: videoDimensions.width,
        bottom: videoDimensions.height,
      },
    };
  });
}

export function selectPreset(
  state: EditSceneModalState,
  preset: ScenePreset,
  newSlots: LayoutSegmentSlot[],
): EditSceneModalState {
  return {
    ...state,
    selectedPreset: preset,
    slotCount: newSlots.length,
    slots: newSlots,
  };
}

export function getAutoCaptionsPosition(
  slotCount: number | undefined,
  captionsSize: Size<ViewportHeight, ViewportWidth>,
): Position<string> | undefined {
  if (slotCount === undefined || !captionsSize) {
    return null;
  }

  // only use auto-positioning for >= 2 subjects
  if (slotCount < 2) {
    return null;
  }

  const top = 50 - captionsSize.height.value / 2;
  const left = 50 - captionsSize.width.value / 2;

  return {
    top: `${top}vh`,
    left: `${left}vw`,
  };
}

export function createCaptionsOverrideState(
  override?: LayoutSegmentCaption,
  defaultValue?: SceneCaptionsOverride,
): Required<Pick<EditSceneModalState, 'captionsOverride'>> | undefined {
  if (!override || !override.region?.properties) {
    return { captionsOverride: defaultValue };
  }

  return {
    captionsOverride: {
      position: override.region.properties,
    },
  };
}

export function createLayoutSegmentCaptionFromOverride(
  captionsOverride: SceneCaptionsOverride,
): LayoutSegmentCaption | undefined {
  if (!captionsOverride) {
    return null;
  }

  return {
    region: {
      properties: captionsOverride.position,
    },
  };
}

export function createLayoutSegmentPatchFromState(
  state: EditSceneModalState,
): LayoutSegmentPatch {
  const isBackgroundBlurEnabled = isBackgroundBlurEnabledSelector(state);
  const isBackgroundBlurDirty = isBackgroundBlurDirtySelector(state);

  const isCaptionsOverrideDirty = isCaptionsOverrideDirtySelector(state);
  const captionsOverride = captionsOverrideSelector(state);

  const slotCount = slotCountSelector(state);
  const slots = slotsSelector(state).slice(0, slotCount);
  const slotsToSave = slots.slice(0, slotCount);

  const backfill = isBackgroundBlurDirty
    ? { backfill: { enableBlur: isBackgroundBlurEnabled } }
    : undefined;

  // the `?? null` converts undefined to null so that the action creator knows to
  // remove the caption from the segment rather than omit it from the patch
  const caption = isCaptionsOverrideDirty
    ? { caption: createLayoutSegmentCaptionFromOverride(captionsOverride) }
    : undefined;

  return {
    slots: slotsToSave,
    ...backfill,
    ...caption,
  };
}
